import { motion } from "framer-motion";
import {
  MoveUpRight, Grip, ArrowDownToLine, Package, RotateCw, Infinity as InfinityIcon, ArrowRight,
} from "lucide-react";
import { CAPABILITIES } from "@/data/content";
import { SectionHead, Reveal } from "@/components/Reveal";

const ICONS = { MoveUpRight, Grip, ArrowDownToLine, Package, RotateCw, Infinity: InfinityIcon };

function CardContent({ cap, active }) {
  const Icon = ICONS[cap.icon];
  return (
    <div className="relative z-10 h-full flex flex-col justify-between p-6 lg:p-7">
      <div className="flex items-start justify-between">
        <span className="font-display text-lg tracking-widest text-zinc-500 transition-colors duration-500">
          {cap.num}
        </span>
        <Icon size={26} strokeWidth={1.5} className="text-volt" />
      </div>
      <div>
        <h3 className="font-display uppercase text-4xl lg:text-5xl leading-[0.9]">{cap.title}</h3>
        <div
          className={`grid transition-all duration-700 ease-out ${
            active ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <p className="text-sm text-zinc-300 leading-relaxed max-w-xs">{cap.desc}</p>
            <ul className="mt-4 space-y-1.5">
              {cap.exercises.map((ex) => (
                <li key={ex} className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-zinc-400">
                  <ArrowRight size={12} className="text-volt shrink-0" /> {ex}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Capabilities() {
  return (
    <section id="capabilities" data-testid="capabilities-section" className="bg-ink py-24 sm:py-32 px-5 sm:px-10 lg:px-16">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <SectionHead overline="The 6 Capabilities">
          6 Movements.
          <span className="block text-stroke">360° Capability.</span>
        </SectionHead>
        <Reveal delay={0.15}>
          <p className="text-zinc-400 max-w-sm text-sm sm:text-base leading-relaxed">
            You don't just perform exercises here. You train movements, understand their
            purpose, and learn to apply them to real life.
          </p>
        </Reveal>
      </div>

      {/* Desktop: expanding accordion */}
      <div className="hidden lg:flex gap-2 h-[72vh] min-h-[540px]">
        {CAPABILITIES.map((cap) => (
          <div
            key={cap.id}
            data-testid={`capability-card-${cap.id}`}
            className="relative overflow-hidden border border-zinc-800 cursor-default"
            style={{ flexGrow: 1, flexBasis: 0 }}
          >
            <img
              src={cap.image}
              alt={`${cap.title} training`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-45 scale-100"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
            <CardContent cap={cap} active={true} />
          </div>
        ))}
      </div>

      {/* Mobile: stacked */}
      <div className="lg:hidden flex flex-col gap-3">
        {CAPABILITIES.map((cap, i) => {
          const Icon = ICONS[cap.icon];
          return (
            <motion.div
              key={cap.id}
              data-testid={`capability-card-mobile-${cap.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden border border-zinc-800 p-6 min-h-[220px] flex flex-col justify-end"
            >
              <img src={cap.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-30" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/20" />
              <div className="absolute top-5 left-6 right-6 flex items-start justify-between">
                <span className="font-display text-lg tracking-widest text-volt">{cap.num}</span>
                <Icon size={24} strokeWidth={1.5} className="text-volt" />
              </div>
              <div className="relative">
                <h3 className="font-display uppercase text-4xl leading-[0.9]">{cap.title}</h3>
                <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{cap.desc}</p>
                <p className="mt-3 text-xs uppercase tracking-[0.14em] text-zinc-500">
                  {cap.exercises.join(" · ")}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
